import React from 'react';
import {
  Box,
  Typography,
  Avatar,
  Chip,
  Paper,
  Divider,
  CircularProgress,
  Grid,
  Button,
} from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Message as MessageIcon,
  Forum as ConversationIcon,
  Schedule as ClockIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  SentimentSatisfiedAltOutlined as SentimentIcon,
  TrendingUp as TrendingIcon,
  Chat as ChatIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SubPageLayout from '../../components/SubPageLayout';
import { formatDate, formatPhone, formatRelative } from '../../utils/formatters';
import { useCustomerDetailsQuery } from './hooks/useCustomerDetailsQuery';
import { getDepartmentName, useDepartments } from '../../hooks/useDepartments';
import TemperatureChip from '../../components/TemperatureChip';
import { sentimentChipSx } from '../../utils/sentiment';
import {
  getTemperatureFromInsight,
  getTemperatureColorFromInsight,
} from '../../components/TemperatureChip/temperature';

const CustomerDetails = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const { data: contact, isLoading } = useCustomerDetailsQuery(id);
  const { data: departments = [] } = useDepartments();

  const handleBack = () => {
    navigate(location.state?.from || '/clients');
  };

  const handleChatClick = () => {
    if (contact?.active_lead_uuid) {
      navigate(`/leads/${contact.active_lead_uuid}`, { state: { from: location.pathname } });
    }
  };

  // Histórico read-only do lead (mesma tela de preview usada na lista de Leads).
  const handleHistoryClick = () => {
    if (contact?.active_lead_uuid) {
      navigate(`/leads/${contact.active_lead_uuid}/preview`, { state: { from: location.pathname } });
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const formatRelativeTime = (dateString) => formatRelative(dateString, i18n.language, { empty: t('notAvailable') });

  const getSentimentLabel = (sentiment) => {
    if (sentiment === null || sentiment === undefined) return t('notAvailable');
    if (sentiment > 0) return t('leadDetails.sentimentPositive');
    if (sentiment < 0) return t('leadDetails.sentimentNegative');
    return t('leadDetails.sentimentNeutral');
  };

  const getPurposeLabel = (purpose) => {
    const purposes = {
      sale: t('leadDetails.purposeSale'),
      question: t('leadDetails.purposeQuestion'),
      schedule: t('leadDetails.purposeSchedule'),
      undefined: t('leadDetails.purposeUndefined'),
    };
    return purposes[purpose] || purpose || t('notAvailable');
  };

  // Loading and not-found keep the app bar, so "back" is always there.
  if (isLoading) {
    return (
      <SubPageLayout title={t('leadDetails.title')} onBack={handleBack}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </SubPageLayout>
    );
  }

  if (!contact) {
    return (
      <SubPageLayout title={t('leadDetails.title')} onBack={handleBack}>
        <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ py: 6 }}>
          {t('leadDetails.contactNotFound')}
        </Typography>
      </SubPageLayout>
    );
  }

  return (
    <SubPageLayout title={t('leadDetails.title')} onBack={handleBack}>
      {/* Customer Info Card */}
      <Paper sx={{ p: 3, mb: 2, textAlign: 'center' }}>
        <Avatar
          sx={{
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            width: 80,
            height: 80,
            mx: 'auto',
            mb: 2,
          }}
        >
          {getInitials(contact.display_name)}
        </Avatar>

        <Typography variant="h6" sx={{ mb: 0.5 }}>
          {contact.display_name || t('notAvailable')}
        </Typography>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          {t('leadDetails.customerSince')}: {formatDate(contact.created_at, i18n.language, { empty: t('notAvailable') })}
        </Typography>

        {/* TODO v1: lead status chips — hidden for v0
        <Stack direction="row" spacing={1} justifyContent="center" useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
          {contact.active_lead_status && (
            <Chip
              label={getStatusLabel(contact.active_lead_status)}
              color={getStatusColor(contact.active_lead_status)}
              size="small"
              sx={{ fontSize: '0.75rem' }}
            />
          )}
          {contact.is_currently_assigned_to_me && (
            <Chip
              label={t('clients.assignedToMe')}
              size="small"
              sx={{
                fontSize: '0.7rem',
                bgcolor: (theme) => theme.alpha((theme.vars || theme).palette.primary.main, 0.12),
                color: 'primary.main',
                '& .MuiChip-label': { px: 1 },
              }}
            />
          )}
          {contact.active_lead_uuid && (
            <Chip
              label={t('leadDetails.hasActiveConversation')}
              variant="outlined"
              color="success"
              size="small"
              sx={{ fontSize: '0.75rem', cursor: 'pointer' }}
              onClick={handleChatClick}
            />
          )}
        </Stack>
        */}

        {/* Botão de mensagens do lead ativo — varia conforme a relação do
            colaborador com o lead:
            - atendendo agora (is_currently_assigned_to_me) → chat ativo;
            - já participou antes (times_attended_by_me > 0) → histórico read-only;
            - sem interação prévia → nenhum botão.
            TODO backend: idealmente o serializer de Contact deveria expor
            capabilities de autorização (can_view_messages / can_view_history);
            enquanto não houver, inferimos pelos campos abaixo. */}
        {contact.active_lead_uuid && contact.is_currently_assigned_to_me && (
          <Box sx={{ mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<ChatIcon />}
              onClick={handleChatClick}
              fullWidth
              sx={{
                borderRadius: 2,
                py: 1,
              }}
            >
              {t('clients.openActiveChat')}
            </Button>
          </Box>
        )}
        {contact.active_lead_uuid
          && !contact.is_currently_assigned_to_me
          && contact.relationship?.times_attended_by_me > 0 && (
          <Box sx={{ mb: 2 }}>
            <Button
              variant="contained"
              color="leadHistory"
              startIcon={<VisibilityIcon />}
              onClick={handleHistoryClick}
              fullWidth
              sx={{
                borderRadius: 2,
                py: 1,
              }}
            >
              {t('leads.view_history')}
            </Button>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Contact Info */}
        <Box sx={{ textAlign: 'left' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
            <PhoneIcon fontSize="small" color="action" />
            <Typography variant="body2">{contact.phone_number ? formatPhone(contact.phone_number) : t('notAvailable')}</Typography>
          </Box>

          {contact.email && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <EmailIcon fontSize="small" color="action" />
              <Typography variant="body2">{contact.email}</Typography>
            </Box>
          )}

          {(contact.city || contact.state) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <LocationIcon fontSize="small" color="action" />
              <Typography variant="body2">
                {[contact.city, contact.state].filter(Boolean).join(', ')}
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Statistics Cards */}
      {contact.stats && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            {t('leadDetails.statistics')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'action.hover', borderRadius: 2 }}>
                <ConversationIcon sx={{ color: 'primary.main', mb: 0.5 }} />
                <Typography variant="h6">
                  {contact.stats.total_conversations || 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('leadDetails.conversations')}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'action.hover', borderRadius: 2 }}>
                <MessageIcon sx={{ color: 'primary.main', mb: 0.5 }} />
                <Typography variant="h6">
                  {contact.stats.total_messages || 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('leadDetails.messages')}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'action.hover', borderRadius: 2 }}>
                <ClockIcon sx={{ color: 'primary.main', mb: 0.5 }} />
                <Typography variant="caption" sx={{ display: 'block' }}>
                  {t('leadDetails.firstSeen')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {contact.stats.first_seen_at ? formatRelativeTime(contact.stats.first_seen_at) : t('notAvailable')}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'action.hover', borderRadius: 2 }}>
                <ClockIcon sx={{ color: 'primary.main', mb: 0.5 }} />
                <Typography variant="caption" sx={{ display: 'block' }}>
                  {t('leadDetails.lastMessage')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {contact.stats.last_message_at ? formatRelativeTime(contact.stats.last_message_at) : t('notAvailable')}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Relationship Info */}
      {contact.relationship && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <PersonIcon color="action" />
            <Typography variant="subtitle2">
              {t('leadDetails.relationshipInfo')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {t('leadDetails.timesAttendedByMe')}:
              </Typography>
              <Typography variant="subtitle2">
                {contact.relationship.times_attended_by_me || 0}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {t('leadDetails.lastAttendedAt')}:
              </Typography>
              <Typography variant="subtitle2">
                {contact.relationship.last_attended_at ? formatRelativeTime(contact.relationship.last_attended_at) : t('notAvailable')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <GroupIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {t('leadDetails.totalCollaborators')}:
                </Typography>
              </Box>
              <Typography variant="subtitle2">
                {contact.relationship.collaborators_count || 0}
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* AI Insights */}
      {contact.ai_insights && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <TrendingIcon color="action" />
            <Typography variant="subtitle2">
              {t('leadDetails.aiInsights')}
            </Typography>
          </Box>

          {/* Temperature and Sentiment */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            {!!contact.ai_insights.last_temperature && (
              <TemperatureChip
                hasInsight
                temperatureValue={contact.ai_insights.last_temperature}
                temperature={getTemperatureFromInsight(contact.ai_insights.last_temperature)}
                temperatureColor={getTemperatureColorFromInsight(contact.ai_insights.last_temperature)}
              />
            )}
            {contact.ai_insights.last_sentiment !== null && (
              <Chip
                icon={<SentimentIcon />}
                label={`${t('leadDetails.sentiment')}: ${getSentimentLabel(contact.ai_insights.last_sentiment)}`}
                sx={sentimentChipSx(contact.ai_insights.last_sentiment)}
              />
            )}
          </Box>

          {/* Department: o insight guarda o internal_name, não o nome exibido. */}
          {contact.ai_insights.last_department && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                {t('leadDetails.department')}:
              </Typography>
              <Box display="flex" alignItems="center" gap={0.5}>
                <Typography variant="subtitle2">
                  {getDepartmentName(departments, contact.ai_insights.last_department)}
                </Typography>
              </Box>
            </Box>
          )}

          {contact.ai_insights.last_target_business && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                {t('leadDetails.targetBusiness')}:
              </Typography>
              <Typography variant="subtitle2">
                {contact.ai_insights.last_target_business}
              </Typography>
            </Box>
          )}

          {/* Purpose */}
          {contact.ai_insights.last_purpose && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                {t('leadDetails.purpose')}:
              </Typography>
              <Typography variant="subtitle2">
                {getPurposeLabel(contact.ai_insights.last_purpose)}
              </Typography>
            </Box>
          )}

          {/* Summary */}
          {contact.ai_insights.last_summary && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                {t('leadDetails.summary')}:
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {contact.ai_insights.last_summary}
              </Typography>
            </Box>
          )}

          {/* Decision Deadline */}
          {contact.ai_insights.decision_deadline_days !== null && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t('leadDetails.decisionDeadline')}:
              </Typography>
              <Typography variant="subtitle2">
                {contact.ai_insights.decision_deadline_days} {t('leadDetails.days')}
              </Typography>
            </Box>
          )}

          {/* Show message if no AI insights */}
          {contact.ai_insights.last_temperature === null &&
           contact.ai_insights.last_sentiment === null &&
           !contact.ai_insights.last_department &&
           !contact.ai_insights.last_target_business &&
           !contact.ai_insights.last_purpose &&
           !contact.ai_insights.last_summary &&
           contact.ai_insights.decision_deadline_days === null && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {t('leadDetails.noAiInsightsYet')}
              </Typography>
            </Box>
          )}
        </Paper>
      )}
    </SubPageLayout>
  );
};

export default CustomerDetails;
